import React, { useEffect, useMemo, useState } from 'react';
import {
  CircleStackIcon,
  CpuChipIcon,
  GlobeAmericasIcon,
  HeartIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  FingerPrintIcon,
  UserCircleIcon,
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import routes_web from '../../../connections/routes_web';
import { useAuth } from '../../../connections/AuthContext';
import { getAssistantCard } from '../../identity/moduleServices/identity_services';

// Card height — +10% over the original h-[5.5rem]/h-32 (88px/128px → ~97px/141px).
const CARD_HEIGHT = 'h-[6.05rem] md:h-[8.8rem]';

// Module Card Component
const ModuleCard = ({ icon: Icon, title, path, className = '' }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(path)}
      className={`${CARD_HEIGHT} bg-white flex flex-col items-center justify-center hover:shadow-md transition rounded-xl p-3 cursor-pointer ${className}`}
    >
      <Icon className="w-6 h-6 text-gray-700 mb-1" />
      <span className="text-lg font-semibold">{title}</span>
    </div>
  );
};

// Three discreet pulsing dots — placeholder while the avatar image loads.
// Mirrors gabarito-app's ImageLoadingDots (frontend/src/views/bares/common/components/ImageLoadingDots.tsx)
// and its `.gs-loading-dots`/`.gs-loading-dot` keyframes, adapted to inline
// Tailwind (Quinas App has no equivalent design-system.css utility yet).
const ImageLoadingDots = () => (
  <div role="status" aria-label="Carregando imagem" className="flex items-center justify-center gap-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1 w-1 rounded-full bg-blue-500 animate-[gs-loading-dot-pulse_1.05s_ease-in-out_infinite]"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

// Avatar with three-dots loading state + graceful fallback to a generic
// placeholder icon on missing/broken image. Same pattern as gabarito-app's
// PratoThumb: opacity-0 → opacity-100 on load, dots shown until then.
const AssistantAvatar = ({ src, alt, className = '', iconClassName = '' }) => {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setBroken(false);
  }, [src]);

  const showImg = Boolean(src && !broken);

  return (
    <div
      className={`rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden relative ${className}`}
    >
      {showImg ? (
        <>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <ImageLoadingDots />
            </div>
          )}
          <img
            src={src}
            alt={alt}
            className={`h-full w-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
          />
        </>
      ) : (
        <UserCircleIcon className={`text-blue-300 ${iconClassName}`} />
      )}
    </div>
  );
};

// Assistant profile-style card — replaces the plain Identity ModuleCard
// when a synced assistant-card snapshot is available (see
// identity.services.get_assistant_card). Falls back to the generic
// ModuleCard when unavailable (profile never synced / GCS unreachable),
// so the slot never renders empty. Icons match tab_overview/main_overview.jsx
// (CpuChipIcon = engines, CircleStackIcon = data sources) for consistency.
const AssistantCard = ({ name, avatarUrl, activeEngines, totalEngines, dataSources, path, className = '' }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(path)}
      className={`${CARD_HEIGHT} bg-white flex items-center hover:shadow-md transition rounded-xl px-4 py-3 cursor-pointer gap-3 ${className}`}
    >
      <AssistantAvatar
        src={avatarUrl}
        alt={name}
        className="h-12 w-12 md:h-16 md:w-16"
        iconClassName="h-8 w-8 md:h-10 md:w-10"
      />
      <div className="min-w-0 flex-1">
        <span className="block text-lg font-semibold truncate">{name}</span>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <CpuChipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            {activeEngines}/{totalEngines} engines
          </span>
          <span className="inline-flex items-center gap-1">
            <CircleStackIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            {dataSources} sources
          </span>
        </div>
      </div>
    </div>
  );
};

// Skeleton Loading Component
const ModuleCardSkeleton = ({ className = '' }) => (
  <div
    className={`${CARD_HEIGHT} bg-white flex flex-col items-center justify-center rounded-xl p-3 animate-pulse ${className}`}
  >
    <div className="w-6 h-6 bg-gray-300 rounded mb-1"></div>
    <div className="h-5 w-20 bg-gray-300 rounded"></div>
  </div>
);

// Icon and route map
const MODULES_CONFIG = {
  core: { icon: CpuChipIcon, title: 'Core', path: routes_web.core_main },
  health: { icon: HeartIcon, title: 'Health', path: routes_web.health_main },
  travel: { icon: GlobeAmericasIcon, title: 'Travel', path: routes_web.travel_main },
  planner: { icon: CalendarDaysIcon, title: 'Planner', path: routes_web.planner_main },
  finance: { icon: BanknotesIcon, title: 'Finance', path: routes_web.finance_main },
  notepad: { icon: DocumentTextIcon, title: 'Notepad', path: routes_web.notepad_main },
  identity: { icon: FingerPrintIcon, title: 'Identity', path: routes_web.identity_main },
};

const MODULES_GAP = 'gap-2';
// Mobile 2-col → Identity spans full row (2×1). Desktop 4-col → half width (2 of 4).
const IDENTITY_CARD_WIDTH = 'w-full md:max-w-[calc(50%-0.25rem)]';

// localStorage cache for the assistant card — stale-while-revalidate: render
// the last known snapshot instantly on mount (no loading flash), then always
// refetch in the background and update once the real response lands. Same
// "show local first, sync when online responds" principle as the app's
// offline-first modules (Dexie), scoped down to a single cached payload
// since this card has no write path of its own to reconcile.
const ASSISTANT_CARD_CACHE_PREFIX = 'qapp:assistant-card:';

const readAssistantCardCache = (userId) => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(`${ASSISTANT_CARD_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.available ? parsed : null;
  } catch {
    return null;
  }
};

const writeAssistantCardCache = (userId, data) => {
  if (!userId) return;
  try {
    if (data?.available) {
      localStorage.setItem(`${ASSISTANT_CARD_CACHE_PREFIX}${userId}`, JSON.stringify(data));
    } else {
      localStorage.removeItem(`${ASSISTANT_CARD_CACHE_PREFIX}${userId}`);
    }
  } catch {
    // Storage unavailable/full — cache is a convenience, safe to skip.
  }
};

// PrincipalTab Component
const TabPrincipal = () => {
  const { user } = useAuth();
  const preferences = user?.app_preferences || [];
  // 'idle' | 'loading' | 'ready' | 'unavailable'
  const [assistantStatus, setAssistantStatus] = useState('idle');
  const [assistantCard, setAssistantCard] = useState(null);

  const visibleModules = useMemo(
    () =>
      preferences
        .filter((pref) => pref.app_visible && pref.app_enabled)
        .sort((a, b) => a.app_order - b.app_order),
    [preferences]
  );

  const identityModule = useMemo(
    () => visibleModules.find((pref) => pref.app_key === 'identity'),
    [visibleModules]
  );
  const otherModules = useMemo(
    () => visibleModules.filter((pref) => pref.app_key !== 'identity'),
    [visibleModules]
  );

  useEffect(() => {
    if (!user || !identityModule) {
      setAssistantStatus('idle');
      setAssistantCard(null);
      return;
    }

    let cancelled = false;

    // Render the last known snapshot immediately — no loading flash for a
    // returning user. A background fetch below reconciles it with the
    // server's current state.
    const cached = readAssistantCardCache(user.id);
    if (cached) {
      setAssistantCard(cached);
      setAssistantStatus('ready');
    } else {
      setAssistantStatus('loading');
      setAssistantCard(null);
    }

    getAssistantCard()
      .then((data) => {
        if (cancelled) return;
        if (data?.available) {
          setAssistantCard(data);
          setAssistantStatus('ready');
          writeAssistantCardCache(user.id, data);
        } else if (!cached) {
          // No cache to fall back on — genuinely unavailable (never synced).
          setAssistantCard(null);
          setAssistantStatus('unavailable');
          writeAssistantCardCache(user.id, data);
        }
        // else: request came back unavailable but we have a cached snapshot
        // — keep showing it rather than flashing to the generic fallback.
      })
      .catch(() => {
        if (cancelled) return;
        if (!cached) {
          setAssistantCard(null);
          setAssistantStatus('unavailable');
        }
        // else: offline/error but cache exists — keep showing cached data.
      });

    return () => {
      cancelled = true;
    };
  }, [user, identityModule]);

  // Show skeleton while loading
  if (!user) {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-4 ${MODULES_GAP} w-full`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <ModuleCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const identityConfig = identityModule
    ? MODULES_CONFIG[identityModule.app_key]
    : null;

  return (
    <>
      {identityConfig && (
        <div className="flex justify-center w-full mb-3">
          {assistantStatus === 'loading' || assistantStatus === 'idle' ? (
            <ModuleCardSkeleton className={IDENTITY_CARD_WIDTH} />
          ) : assistantStatus === 'ready' && assistantCard ? (
            <AssistantCard
              name={assistantCard.display_name}
              avatarUrl={assistantCard.avatar_url}
              activeEngines={assistantCard.active_engines_count}
              totalEngines={assistantCard.total_engines_count}
              dataSources={assistantCard.data_sources_count}
              path={identityConfig.path}
              className={IDENTITY_CARD_WIDTH}
            />
          ) : (
            <ModuleCard
              icon={identityConfig.icon}
              title={identityConfig.title}
              path={identityConfig.path}
              className={IDENTITY_CARD_WIDTH}
            />
          )}
        </div>
      )}

      <div className={`grid grid-cols-2 md:grid-cols-4 ${MODULES_GAP} w-full`}>
        {visibleModules.length === 0 ? (
          <div className="col-span-2 md:col-span-4 text-center py-8 text-gray-500">
            No modules available
          </div>
        ) : (
          otherModules.map((pref) => {
            const config = MODULES_CONFIG[pref.app_key];
            if (!config) return null;
            return (
              <ModuleCard
                key={pref.app_key}
                icon={config.icon}
                title={config.title}
                path={config.path}
              />
            );
          })
        )}
      </div>
    </>
  );
};

export default TabPrincipal;
